import { ShoppingCart, ChevronDown } from 'lucide-react';

const LOGO_URL = "/images/cave-city-pawn-logo.png";
const CAVEMAN_COUPLE = "/images/cavemen-couple.png";
const CAVEMAN_CARRY = "/images/caveman-carrying.png";

export default function HeroSection() {
  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      aria-label="Welcome to Cave City Pawn Shop"
    >
      {/* Cave dark background */}
      <div className="absolute inset-0 cave-gradient" />

      {/* Stone texture overlay */}
      <div className="absolute inset-0 noise-overlay" />

      {/* Stalactite decorations at top */}
      <div className="absolute top-0 left-0 right-0 flex justify-between px-4">
        {[...Array(8)].map((_, i) => (
          <div
            key={i}
            className="w-4 md:w-6 bg-gradient-to-b from-dirt-brown to-stone-dark opacity-40"
            style={{
              height: `${30 + Math.random() * 50}px`,
              borderRadius: '0 0 50% 50%',
              marginTop: '-2px',
            }}
          />
        ))}
      </div>

      {/* Caveman character - left */}
      <img
        src={CAVEMAN_COUPLE}
        alt="Fun cartoon caveman couple - Cave City Pawn mascots"
        className="absolute bottom-8 left-2 md:left-8 w-32 md:w-52 lg:w-64 caveman-float opacity-90 z-10 hidden sm:block"
        loading="lazy"
      />

      {/* Caveman character - right */}
      <img
        src={CAVEMAN_CARRY}
        alt="Cartoon caveman carrying cavewoman - Prehistoric fun at Cave City Pawn"
        className="absolute bottom-8 right-2 md:right-8 w-28 md:w-44 lg:w-56 caveman-float-delayed opacity-90 z-10 hidden sm:block"
        loading="lazy"
      />

      {/* Main content */}
      <div className="relative z-20 text-center px-4 sm:px-6 max-w-4xl mx-auto pt-24 md:pt-20">
        {/* Logo */}
        <div className="mb-8 md:mb-10 hero-anim-logo">
          <img
            src={LOGO_URL}
            alt="Cave City Pawn Shop & Trading Company - Established 2020"
            className="mx-auto w-64 md:w-80 lg:w-[420px] drop-shadow-2xl"
            width={420}
            height="auto"
          />
        </div>

        {/* Tagline */}
        <h1 className="hero-anim-h1 text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading text-bone-white uppercase tracking-wide mb-5 drop-shadow-lg leading-tight">
          The Best Prehistoric<br />
          <span className="text-accent">Deals in Town!</span>
        </h1>

        <p className="hero-anim-p text-base md:text-lg text-stone-light font-body max-w-2xl mx-auto mb-10 leading-relaxed">
          Buy, sell, or pawn firearms, electronics, musical instruments, jewelry &amp; power tools. 
          Over <strong className="text-accent">35 years of experience</strong> with no credit check pawn loans. 
          Your trusted neighborhood pawn shop!
        </p>

        {/* CTA Buttons */}
        <div className="hero-anim-cta flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-5 mb-12">
          <a
            href="https://alabamapawnandstorage.com/pawn-shop"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-shop-online-btn"
            className="cave-btn inline-flex items-center gap-3 px-9 py-4 bg-magma-red text-white font-heading text-lg uppercase tracking-wider rounded-rock shadow-deep hover:shadow-none hover:translate-x-1 hover:translate-y-1 transition-all duration-300"
          >
            <ShoppingCart size={22} />
            Shop Online Now
          </a>
          <a
            href="#pawn-process"
            data-testid="hero-learn-more-btn"
            className="cave-btn inline-flex items-center gap-2 px-9 py-4 border-4 border-bone-white text-bone-white font-heading text-lg uppercase tracking-wider rounded-rock hover:bg-bone-white hover:text-dirt-brown transition-all duration-300"
          >
            Sell or Pawn
          </a>
        </div>

        {/* Quick info pills */}
        <div className="hero-anim-pills flex flex-wrap justify-center gap-3 mb-10">
          {['No Credit Checks', 'Same Day Cash', '35+ Years Exp.', 'Archery Gear'].map((tag) => (
            <span
              key={tag}
              className="px-4 py-1.5 bg-dirt-brown/50 text-stone-light text-sm font-body font-semibold rounded-full border border-stone-dark/30 backdrop-blur-sm"
            >
              {tag}
            </span>
          ))}
        </div>

        {/* Scroll indicator */}
        <a href="#categories" className="hero-anim-scroll inline-block animate-bounce" aria-label="Scroll down">
          <ChevronDown size={32} className="text-stone-light opacity-50" />
        </a>
      </div>

      {/* Boulder decorations at bottom */}
      <div className="absolute bottom-0 left-0 right-0 z-10">
        <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
          <path d="M0 120L60 108C120 96 240 72 360 66C480 60 600 72 720 78C840 84 960 84 1080 78C1200 72 1320 60 1380 54L1440 48V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="#F4EBD9"/>
        </svg>
      </div>
    </section>
  );
}
